import { Influencer } from "@foodfluence/shared";

export const isCreatorUGCOnly = (influencerData: Influencer) => {
    return influencerData?.creatorTypes?.includes('ugc') && influencerData?.creatorTypes?.length === 1;
}

// ============================================
// Influencer Name/Handle Utility Functions
// ============================================

/**
 * Extracts and validates the influencer's first name.
 * Returns undefined if the firstName is empty, whitespace-only, or "unknown".
 * 
 * @param influencer - The influencer data object (can be partial or undefined)
 * @returns The validated first name, or undefined if invalid
 */
export function getInfluencerFirstName(influencer?: Partial<Influencer> | null): string | undefined {
  if (!influencer) return undefined;
  
  if (
    influencer.firstName &&
    influencer.firstName.trim() !== '' &&
    influencer.firstName.toLowerCase() !== 'unknown'
  ) {
    return influencer.firstName.trim();
  }
  return undefined;
}

/**
 * Gets a display name for the influencer, falling back through multiple options:
 * 1. First name (if valid)
 * 2. Instagram handle (with optional @ prefix)
 * 3. TikTok handle (with optional @ prefix)
 * 4. "Unknown" as a fallback
 * 
 * This should be used everywhere we need to display an influencer's name/handle.
 * 
 * @param influencer - The influencer data object (can be partial or undefined)
 * @param lowercase - Whether to return lowercase (default: false)
 * @returns A display name for the influencer
 */
export function getInfluencerDisplayName(influencer?: Partial<Influencer> | null, lowercase: boolean = false): string {
  if (!influencer) return 'Unknown';
  
  const firstName = getInfluencerFirstName(influencer);
  
  if (firstName) {
    return lowercase ? firstName.toLowerCase() : firstName;
  }
  
  // Fall back to handles
  if (hasValidInstagramHandle(influencer)) {
    const handle = `@${influencer.instagramHandle}`;
    return lowercase ? handle.toLowerCase() : handle;
  }

  if (hasValidTikTokHandle(influencer)) {
    const handle = `@${influencer.tiktokHandle}`;
    return lowercase ? handle.toLowerCase() : handle;
  }

  return 'Unknown';
}

/**
 * Checks if a handle is valid (not empty, not "unknown")
 * @param handle - The handle to validate
 * @returns true if the handle is valid
 */
function isValidHandle(handle?: string | null): boolean {
  return !!(
    handle &&
    handle.trim() !== '' &&
    handle.toLowerCase() !== 'unknown'
  );
}

/**
 * Gets the primary social media handle for the influencer.
 * Prefers Instagram handle, falls back to TikTok handle.
 * Returns empty string if neither exists (for API compatibility).
 * 
 * Use this when you specifically need a social handle (e.g., for @ mentions, API calls)
 * rather than a display name (which might be a first name).
 * 
 * @param influencer - The influencer data object (can be partial or undefined)
 * @returns The primary handle, or empty string if none exists
 */
export function getInfluencerPrimaryHandle(influencer?: Partial<Influencer> | null): string {
  if (!influencer) return '';
  
  if (isValidHandle(influencer.instagramHandle)) {
    return influencer.instagramHandle!;
  }

  if (isValidHandle(influencer.tiktokHandle)) {
    return influencer.tiktokHandle!;
  }

  return '';
}

/**
 * Checks if an influencer has a valid Instagram handle
 * @param influencer - The influencer data object (can be partial or undefined)
 * @returns true if the influencer has a valid Instagram handle
 */
export function hasValidInstagramHandle(influencer?: Partial<Influencer> | null): boolean {
  return isValidHandle(influencer?.instagramHandle);
}

/**
 * Checks if an influencer has a valid TikTok handle
 * @param influencer - The influencer data object (can be partial or undefined)
 * @returns true if the influencer has a valid TikTok handle
 */
export function hasValidTikTokHandle(influencer?: Partial<Influencer> | null): boolean {
  return isValidHandle(influencer?.tiktokHandle);
}

/**
 * Returns the influencer handle fields that must be filled in before they can
 * propose the given deliverables — i.e. platforms they are proposing content
 * for without having that platform's account on their profile.
 *
 * @param influencer - The influencer data object (can be partial or undefined)
 * @param deliverableCounts - Proposed deliverable counts (tiktoks, instagramReels, instagramPosts, instagramStories)
 * @returns The missing handle field names, empty if none are required
 */
export function getMissingDeliverableHandles(
  influencer: Partial<Influencer> | null | undefined,
  deliverableCounts?: Record<string, number> | null
): ('instagramHandle' | 'tiktokHandle')[] {
  const counts = deliverableCounts || {};
  const missing: ('instagramHandle' | 'tiktokHandle')[] = [];
  const instagramCount = (counts.instagramReels || 0) + (counts.instagramPosts || 0) + (counts.instagramStories || 0);
  if (instagramCount > 0 && !hasValidInstagramHandle(influencer)) missing.push('instagramHandle');
  if ((counts.tiktoks || 0) > 0 && !hasValidTikTokHandle(influencer)) missing.push('tiktokHandle');
  return missing;
}

/**
 * Gets the best follower count for the influencer.
 * Returns the higher of Instagram or TikTok followers.
 * 
 * @param influencer - The influencer data object (can be partial or undefined)
 * @returns The best follower count
 */
export function getInfluencerBestFollowerCount(influencer?: Partial<Influencer> | null): number {
  if (!influencer) return 0;
  
  const instagramFollowers = influencer.follower_count || 0;
  const tiktokFollowers = (influencer as any).tiktokFollowers || 0;
  
  return Math.max(instagramFollowers, tiktokFollowers);
}

// ============================================
// Contact Information Validation Functions
// ============================================

/**
 * Validates that a last name is valid (not empty, not whitespace-only, not "unknown")
 * @param lastNameValue - The last name to validate
 * @returns true if the last name is valid
 */
export const isValidLastName = (lastNameValue?: string | null): boolean => {
  return !!(
    lastNameValue &&
    lastNameValue.trim() !== '' &&
    lastNameValue.toLowerCase() !== 'unknown'
  );
};

/**
 * Validates that an email address is in a valid format
 * @param emailValue - The email address to validate
 * @returns true if the email is valid
 */
export const isValidEmail = (emailValue?: string | null): boolean => {
  if (!emailValue || emailValue.trim() === '') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailValue.trim());
};

/**
 * Validates that a phone number is valid (not empty, at least 10 digits)
 * @param phoneValue - The phone number to validate (national number only)
 * @param countryCodeValue - The country code (optional, for future validation)
 * @returns true if the phone number is valid
 */
export const isValidPhone = (phoneValue: string, countryCodeValue?: string): boolean => {
  return !!(
    phoneValue &&
    phoneValue.trim() !== '' &&
    phoneValue.trim().length >= 10
  );
};

/**
 * Validates that both first and last names are valid
 * @param firstName - The first name to validate
 * @param lastName - The last name to validate
 * @returns true if both names are valid
 */
export const isNameValid = (firstName: string, lastName: string): boolean => {
  return !!(
    getInfluencerFirstName({ firstName } as any) && 
    isValidLastName(lastName)
  );
};

/**
 * Validates all contact information (name, email, phone)
 * @param firstName - The first name to validate
 * @param lastName - The last name to validate
 * @param email - The email address to validate
 * @param phoneNumber - The phone number to validate (national number only)
 * @param countryCode - The country code (optional)
 * @returns true if all contact information is valid
 */
export const isContactInfoValid = (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  countryCode?: string
): boolean => {
  return (
    isNameValid(firstName, lastName) &&
    isValidEmail(email) &&
    isValidPhone(phoneNumber, countryCode)
  );
};